import React from 'react'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import {
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
} from '../../../BuildingCharacteristics.styles'
import { translateGwrValue } from '../../../../../../utils/gwrTranslations'
import type { OccupancyCardProps } from '../OverviewTab.types'

export const OccupancyCard: React.FC<OccupancyCardProps> = ({
  buildingCategory,
  buildingClass,
  popetyClassification,
  numberOfDwellings,
  floorCount,
  height,
  grossFloorArea,
  buildingUse,
  formatArea,
  formatHeight,
  formatNumber,
  t,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Icon name="home" size="small" />
          {t('overview.cards.occupancy.title')}
        </CardTitle>
      </CardHeader>
      <MetricGrid>
        <MetricRow>
          <MetricLabel>
            {t('overview.cards.occupancy.buildingCategory')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('overview.cards.occupancy.tooltips.category.title')}
                shortDescription={t(
                  'overview.cards.occupancy.tooltips.category.description'
                )}
                calculation={t(
                  'overview.cards.occupancy.tooltips.category.calculation'
                )}
                importance={t(
                  'overview.cards.occupancy.tooltips.category.importance'
                )}
                source={t('overview.cards.occupancy.tooltips.category.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {translateGwrValue(buildingCategory, 'buildingCategory', t) ||
              buildingClass ||
              t('common.na')}
          </MetricValue>
        </MetricRow>
        {buildingUse && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.occupancy.primaryUse')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'overview.cards.occupancy.tooltips.primaryUse.title'
                  )}
                  shortDescription={t(
                    'overview.cards.occupancy.tooltips.primaryUse.description'
                  )}
                  calculation={t(
                    'overview.cards.occupancy.tooltips.primaryUse.calculation'
                  )}
                  importance={t(
                    'overview.cards.occupancy.tooltips.primaryUse.importance'
                  )}
                  source={t(
                    'overview.cards.occupancy.tooltips.primaryUse.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {translateGwrValue(buildingUse, 'buildingClass', t)}
            </MetricValue>
          </MetricRow>
        )}
        {popetyClassification && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.occupancy.popetyClassification')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'overview.cards.occupancy.tooltips.popetyClassification.title'
                  )}
                  shortDescription={t(
                    'overview.cards.occupancy.tooltips.popetyClassification.description'
                  )}
                  calculation={t(
                    'overview.cards.occupancy.tooltips.popetyClassification.calculation'
                  )}
                  importance={t(
                    'overview.cards.occupancy.tooltips.popetyClassification.importance'
                  )}
                  source="Popety.io"
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t(`popetyClassificationOptions.${popetyClassification}`, {
                ns: 'common',
                defaultValue: popetyClassification,
              })}
            </MetricValue>
          </MetricRow>
        )}
        {numberOfDwellings !== undefined && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.occupancy.numberOfDwellings')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('overview.cards.occupancy.tooltips.dwellings.title')}
                  shortDescription={t(
                    'overview.cards.occupancy.tooltips.dwellings.description'
                  )}
                  calculation={t(
                    'overview.cards.occupancy.tooltips.dwellings.calculation'
                  )}
                  importance={t(
                    'overview.cards.occupancy.tooltips.dwellings.importance'
                  )}
                  source={t(
                    'overview.cards.occupancy.tooltips.dwellings.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{formatNumber(numberOfDwellings)}</MetricValue>
          </MetricRow>
        )}
        {floorCount > 0 && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.occupancy.avgFloorHeight')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'overview.cards.occupancy.tooltips.avgFloorHeight.title'
                  )}
                  shortDescription={t(
                    'overview.cards.occupancy.tooltips.avgFloorHeight.description'
                  )}
                  calculation={t(
                    'overview.cards.occupancy.tooltips.avgFloorHeight.calculation'
                  )}
                  importance={t(
                    'overview.cards.occupancy.tooltips.avgFloorHeight.importance'
                  )}
                  source={t(
                    'overview.cards.occupancy.tooltips.avgFloorHeight.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {height && floorCount
                ? formatHeight(height / floorCount)
                : t('common.na')}
            </MetricValue>
          </MetricRow>
        )}
        {grossFloorArea > 0 && floorCount > 0 && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.occupancy.avgFloorArea')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'overview.cards.occupancy.tooltips.avgFloorArea.title'
                  )}
                  shortDescription={t(
                    'overview.cards.occupancy.tooltips.avgFloorArea.description'
                  )}
                  calculation={t(
                    'overview.cards.occupancy.tooltips.avgFloorArea.calculation'
                  )}
                  importance={t(
                    'overview.cards.occupancy.tooltips.avgFloorArea.importance'
                  )}
                  source={t(
                    'overview.cards.occupancy.tooltips.avgFloorArea.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{formatArea(grossFloorArea / floorCount)}</MetricValue>
          </MetricRow>
        )}
      </MetricGrid>
    </Card>
  )
}
